import React, { useMemo } from "react";
import { Button } from "antd";
import { ORGANIZED_TOUR_CURATED_GENERIC_TRAVEL_URL } from "../../../utils/organizedTourHeroImageSsot";
import { resolveOrganizedTourPublicHeroDisplayUrl } from "../../../modules/organizedTours/utils/resolveOrganizedTourPublicHeroDisplayUrl";
import {
  formatOrganizedTourPublicOfferIlsWithPrimary,
  resolveOrganizedTourPublicOfferFinalPriceIls,
  resolveOrganizedTourPublicOfferPrimaryFx,
} from "../../../modules/organizedTours/utils/resolveOrganizedTourPublicOfferFinalPriceIls";
import { useAppContext } from "../../../contexts/AppContext";
import OrganizedTourPublicOfferMainTabs from "./OrganizedTourPublicOfferMainTabs";
import { refinePublicOfferParsedModel } from "./publicOfferSectionClassify";
import { safeParseOrganizedTourItinerary } from "./organizedTourPublicOfferTabsModel";

export default function OrganizedTourPublicOfferLanding({
  vm,
  branding,
  cust,
  expiryLabel,
  formatIls,
  onInterest,
  interestBusy,
  interestDone,
  primaryAgentTitle,
  showAgencySubline,
}) {
  const { currencyRate } = useAppContext();

  const parsed = useMemo(() => {
    const raw = safeParseOrganizedTourItinerary(vm.itinerary_text_he);
    return refinePublicOfferParsedModel(raw, vm.itinerary_text_he || "").parsed;
  }, [vm.itinerary_text_he]);

  const displayPrice = useMemo(
    () => resolveOrganizedTourPublicOfferFinalPriceIls(vm),
    [vm]
  );
  const hasPrice = displayPrice.priceIls != null;

  const primaryFx = useMemo(
    () =>
      resolveOrganizedTourPublicOfferPrimaryFx(
        vm,
        displayPrice.priceIls,
        currencyRate
      ),
    [vm, displayPrice.priceIls, currencyRate]
  );

  const priceLine = useMemo(() => {
    if (!hasPrice) return "—";
    return formatOrganizedTourPublicOfferIlsWithPrimary(
      formatIls(displayPrice.priceIls),
      primaryFx.display
    );
  }, [hasPrice, formatIls, displayPrice.priceIls, primaryFx.display]);

  const termsTextHe = useMemo(() => {
    const raw = String(vm.terms_text_he || "");
    return raw
      .replace(/\s*תקנון\s+HotelsRiver\s+ו/gi, " ")
      .replace(/\s*HotelsRiver\s*/gi, " ")
      .replace(/\s{2,}/g, " ")
      .trim();
  }, [vm.terms_text_he]);

  const heroUrl = useMemo(
    () =>
      resolveOrganizedTourPublicHeroDisplayUrl({
        hero_image_url: vm.hero_image_url,
      }),
    [vm.hero_image_url]
  );

  return (
    <div className="organized-tour-public-offer">
      <header className="organized-tour-public-offer__hero">
        <div className="organized-tour-public-offer__hero-bg">
          <img
            className="organized-tour-public-offer__hero-image"
            src={heroUrl}
            alt=""
            loading="eager"
            decoding="async"
            sizes="100vw"
            onError={(e) => {
              e.currentTarget.onerror = null;
              if (e.currentTarget.src !== ORGANIZED_TOUR_CURATED_GENERIC_TRAVEL_URL) {
                e.currentTarget.src = ORGANIZED_TOUR_CURATED_GENERIC_TRAVEL_URL;
              }
            }}
          />
          <div className="organized-tour-public-offer__hero-overlay" aria-hidden />
          <div className="organized-tour-public-offer__hero-content">
            <p className="organized-tour-public-offer__hero-agent">
              {primaryAgentTitle || "הצעה מסוכן מורשה"}
            </p>
            {showAgencySubline && branding?.agencyName ? (
              <p className="organized-tour-public-offer__hero-agency">{branding.agencyName}</p>
            ) : null}
            <h1 className="organized-tour-public-offer__hero-title">
              {vm.tour_title_he || "טיול מאורגן"}
            </h1>
            {vm.route_line_he ? (
              <p className="organized-tour-public-offer__hero-route">
                <span className="organized-tour-public-offer__hero-route-label">מסלול:</span>
                {vm.route_line_he}
              </p>
            ) : null}
            {vm.dates_line_he ? (
              <span className="organized-tour-public-offer__hero-date-badge">{vm.dates_line_he}</span>
            ) : null}
          </div>
          <span className="organized-tour-public-offer__image-disclaimer" role="note">
            התמונה להמחשה בלבד
          </span>
        </div>
      </header>

      <div className="organized-tour-public-offer__body">
        <div className="organized-tour-public-offer__body-grid">
          <main className="organized-tour-public-offer__main">
            <OrganizedTourPublicOfferMainTabs
              itineraryTextHe={vm.itinerary_text_he}
              parsed={parsed}
              additionalInfoHtml={vm.additional_info_html || ""}
              linkedFlights={vm.linked_flights ?? null}
              rooms={vm.rooms ?? null}
              vm={vm}
            />
            <section className="organized-tour-public-offer__section organized-tour-public-offer__terms-block">
              <h2 className="organized-tour-public-offer__section-title organized-tour-public-offer__section-title--accent">
                תנאים והבהרות
              </h2>
              <p className="organized-tour-public-offer__body-text">{termsTextHe}</p>
            </section>
            <p className="organized-tour-public-offer__legal">
              הצגה זו מבוססת על פרטים שנבחרו על ידי נציג. המחיר והזמינות אינם מהווים התחייבות עד להשלמת
              ההזמנה ואימות זמינות מול החברה.
            </p>
          </main>

          <aside className="organized-tour-public-offer__sidebar">
            <div className="organized-tour-public-offer__sidebar-inner">
              <div className="organized-tour-public-offer__price-card organized-tour-public-offer__sidebar-card">
                <div className="organized-tour-public-offer__price-label">מחיר סופי</div>
                <div className="organized-tour-public-offer__price-value">{priceLine}</div>
                {vm.dates_line_he ? (
                  <p className="organized-tour-public-offer__sidebar-dates">{vm.dates_line_he}</p>
                ) : null}
                {branding?.phone ? (
                  <p className="organized-tour-public-offer__sidebar-phone">
                    <a
                      href={`tel:${String(branding.phone).replace(/[^\d+]/g, "")}`}
                      className="organized-tour-public-offer__sidebar-phone-link"
                    >
                      {branding.phone}
                    </a>
                  </p>
                ) : null}
                {cust.customerName || cust.customerPhone ? (
                  <p className="organized-tour-public-offer__sidebar-dates">
                    הצעה ל:
                    {cust.customerName ? <> {cust.customerName}</> : null}
                    {cust.customerPhone ? <> · {cust.customerPhone}</> : null}
                  </p>
                ) : null}
                {expiryLabel ? (
                  <p className="organized-tour-public-offer__expiry">תוקף הקישור: {expiryLabel}</p>
                ) : null}
                <Button
                  type="primary"
                  block
                  size="large"
                  className="ant-btn-organized-offer-main no-print"
                  loading={interestBusy}
                  disabled={interestDone}
                  onClick={onInterest}
                >
                  {interestDone ? "פנייתך נקלטה — תודה!" : "אישור והצטרפות"}
                </Button>
                {branding?.phone || branding?.email || branding?.whatsapp ? (
                  <div className="organized-tour-public-offer__contact-mini no-print">
                    <span className="organized-tour-public-offer__contact-mini-title">יצירת קשר ישיר</span>
                    {branding.phone ? (
                      <a href={`tel:${String(branding.phone).replace(/[^\d+]/g, "")}`}>
                        טלפון · {branding.phone}
                      </a>
                    ) : null}
                    {branding.email ? <a href={`mailto:${branding.email}`}>מייל · {branding.email}</a> : null}
                    {branding.whatsapp ? (
                      <a
                        href={`https://wa.me/${String(branding.whatsapp).replace(/[^\d]/g, "")}`}
                        target="_blank"
                        rel="noreferrer"
                      >
                        וואטסאפ · {branding.whatsapp}
                      </a>
                    ) : null}
                  </div>
                ) : null}
              </div>

            </div>
          </aside>
        </div>
      </div>
    </div>
  );
}
